<script setup>
  import {computed, nextTick, onMounted, onUnmounted, ref} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import PatientinputLayout from "@components/Layout/PatientinputLayout.vue";
  import {useForm} from "@inertiajs/vue3";
  import {confirmationLabels} from "@pages/Patients/Enums/Enums.js";
  import {
    MedButton,
    MedCheckbox,
    MedDescription,
    MedError,
    MedFormField,
    MedLabel,
    MedLink,
  } from "@pharma4u/medicheck-components";
  import SignaturePad from "signature_pad";

  defineOptions({name: "PagesSignPatientDocumentsShow", layout: PatientinputLayout});

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
    client: {
      type: Object,
      required: true,
    },
  });

  const refSignature = ref(null);
  const signaturePad = ref(null);
  const canvas = ref(null);
  const isFullscreen = ref(false);
  const signatureData = ref({
    width: null,
    height: null,
    points: null,
  });

  const message = ref(null);

  const form = useForm({
    datasecurityconfirmed: getConfirmation("datasecurityconfirmed") !== null,
    datasecurityresearchconfirmed: getConfirmation("datasecurityresearchconfirmed") !== null,
    datasecurityaiconfirmed: getConfirmation("datasecurityaiconfirmed") !== null,

    name: props.patient.name,
    isRepresentative: false,
    representative: null,
    city: props?.client?.city, // initialize patient city with client city
    signature: null,
    copy: false,
    email: null,
  });

  onMounted(() => {
    if (refSignature.value) {
      canvas.value = refSignature.value.querySelector("canvas");
      resetCanvas();
      window.addEventListener("resize", resetCanvas);
      globalThis.addEventListener("fullscreenchange", handleFullscreenChange);
    }
  });

  onUnmounted(() => {
    window.removeEventListener("resize", resetCanvas);
    globalThis.removeEventListener("fullscreenchange", handleFullscreenChange);
  });

  const customizedPdfUrl = computed(() => {
    const url = new URL(globalThis.location.href);

    // add "privacy-constent-pdf" to the current pathname
    url.pathname = url.pathname + "/privacy-consent-pdf";

    return url.toString();
  });

  function getConfirmation(subject) {
    return (
      props.patient?.confirmations?.find((confirmation) => {
        return confirmation.subject === subject;
      }) ?? null
    );
  }
  function getHelperText(subject) {
    let confirmation = getConfirmation(subject);
    if (confirmation === null) {
      return null;
    }
    return `Die Einwilligung hierzu wurde von ${confirmation.signer === "patient" ? "Ihnen" : "einer gesetzlichen Vertretung"} bereits am ${confirmation.created_at} abgegeben. Für einen Widerruf wenden Sie sich bitte an Ihre Apotheke.`;
  }

  function resetCanvas() {
    nextTick(() => {
      let originalWidth, originalHeight;

      if (signaturePad.value && !signaturePad.value.isEmpty()) {
        signatureData.value.points = signaturePad.value.toData();
        originalWidth = signatureData.value.width; // Breite des Canvas beim Speichern
        originalHeight = signatureData.value.height; // Höhe des Canvas beim Speichern
      }

      refSignature.value.style.height = Math.round((refSignature.value.offsetWidth / 16) * 9) + "px";
      let ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.value.width = canvas.value.offsetWidth * ratio;
      canvas.value.height = canvas.value.offsetHeight * ratio;
      signatureData.value.width = canvas.value.width;
      signatureData.value.height = canvas.value.height;
      canvas.value.getContext("2d").scale(ratio, ratio);

      if (signaturePad.value === null) {
        signaturePad.value = new SignaturePad(canvas.value, {minDistance: 7, minWidth: 1, maxWidth: 2});
      } else {
        signaturePad.value.clear(); // otherwise isEmpty() might return incorrect value
      }

      if (signatureData.value.points !== null) {
        const currentWidth = signatureData.value.width;
        const currentHeight = signatureData.value.height;

        const scaleX = currentWidth / originalWidth;
        const scaleY = currentHeight / originalHeight;

        signatureData.value.width = currentWidth;
        signatureData.value.height = currentHeight;

        const scaledData = scaleSignatureData(signatureData.value.points, scaleX, scaleY);
        signaturePad.value.fromData(scaledData);
      }
    });
  }

  function handleFullscreenChange() {
    if (!document.fullscreenElement) {
      isFullscreen.value = false;
      resetCanvas();
    }
  }

  function clearSignature() {
    signatureData.value.points = null;
    signatureData.value.width = canvas.value.offsetWidth;
    signatureData.value.height = canvas.value.offsetHeight;
    signaturePad.value.clear();
  }

  /**
   * enter fullscreen and lock landscape
   */
  async function enterFullscreen() {
    if (refSignature.value.requestFullscreen) {
      await refSignature.value.requestFullscreen();
      if (screen.orientation && screen.orientation.lock) {
        try {
          await screen.orientation.lock("landscape");
        } catch (error) {
          console.warn("Orientation lock failed:", error);
        }
      }
      isFullscreen.value = true;
      resetCanvas();
    }
  }

  function exitFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen();
      isFullscreen.value = false;
      resetCanvas();
    }
  }

  function scaleSignatureData(data, scaleX, scaleY) {
    return data.map((group) => ({
      ...group,
      points: group.points.map((p) => ({
        x: p.x * scaleX,
        y: p.y * scaleY,
      })),
    }));
  }

  function save() {
    message.value = null;

    // store
    form
      .transform((data) => ({
        ...data,

        signature: !signaturePad.value.isEmpty() ? signaturePad.value.toSVG() : null,
        representative: data.isRepresentative ? data.representative : null,
      }))
      .post(route("sign-patient-documents.store", {patientId: props.patient.id}), {
        preserveScroll: false, // << false, scroll to top
        onSuccess: (response) => {
          if (response?.props?.flash?.error) {
            message.value = response?.props?.flash?.error;
          } else if (response?.props?.flash?.success) {
            message.value = response?.props?.flash?.success;
          }
        },
      });
  }
</script>

<template>
  <div class="flex h-full flex-col space-y-4 py-8">
    <img src="@images/logo/mc_logo.svg" alt="MediCheck" class="mx-auto w-44" />

    <div v-if="form.wasSuccessful" class="text-center">
      <h1 class="flex items-center justify-center space-x-2 text-2xl font-semibold">
        <ComponentIcon class="text-4xl text-mcgreen">check</ComponentIcon>
        <span>Vielen Dank</span>
      </h1>
      <p class="mt-4">{{ message }}</p>
      <p class="mt-4">Sie können diese Seite jetzt schließen.</p>
    </div>

    <template v-else>
      <h1 class="text-2xl font-semibold">Einwilligungserklärung zur Verarbeitung personenbezogener Daten</h1>

      <ComponentAlert v-if="message" class="mt-3" type="error" :test-id="$page.props.testId">
        <p>{{ message }}</p>
      </ComponentAlert>

      <div class="space-y-1">
        <p>
          Ich habe die
          <MedLink
            variant="primary"
            :href="customizedPdfUrl"
            target="_blank"
            :data-test="getTestId('consent-pdf-link')"
          >
            Einwilligung zur Datenverarbeitung
          </MedLink>
          gelesen.
        </p>
        <p>Hiermit erteile ich meine Einwilligung zur Datennutzung für</p>
      </div>

      <form class="space-y-8" @submit.prevent="save">
        <ul class="space-y-4">
          <li>
            <MedFormField
              orientation="horizontal"
              size="mobile"
              :disabled="getConfirmation('datasecurityconfirmed') !== null"
              :error="form.errors.datasecurityconfirmed"
            >
              <MedCheckbox
                v-model="form.datasecurityconfirmed"
                variant="accent"
                :data-test="getTestId('consent-datasecurity-confirmed-checkbox')"
              />

              <MedLabel>
                {{ confirmationLabels["datasecurityconfirmed"] }}
              </MedLabel>

              <MedDescription v-if="getHelperText('datasecurityconfirmed')">
                {{ getHelperText("datasecurityconfirmed") }}
              </MedDescription>

              <MedError v-if="form.errors.datasecurityconfirmed">
                {{ form.errors.datasecurityconfirmed }}
              </MedError>
            </MedFormField>
          </li>

          <li>
            <MedFormField
              orientation="horizontal"
              size="mobile"
              :disabled="getConfirmation('datasecurityresearchconfirmed') !== null"
              :error="form.errors.datasecurityresearchconfirmed"
            >
              <MedCheckbox
                v-model="form.datasecurityresearchconfirmed"
                variant="accent"
                :data-test="getTestId('consent-datasecurity-research-confirmed-checkbox')"
              />

              <MedLabel>
                {{ confirmationLabels["datasecurityresearchconfirmed"] }}
              </MedLabel>

              <MedDescription v-if="getHelperText('datasecurityresearchconfirmed')">
                {{ getHelperText("datasecurityresearchconfirmed") }}
              </MedDescription>
            </MedFormField>
          </li>

          <li>
            <MedFormField
              orientation="horizontal"
              size="mobile"
              :disabled="getConfirmation('datasecurityaiconfirmed') !== null"
              :error="form.errors.datasecurityaiconfirmed"
            >
              <MedCheckbox
                v-model="form.datasecurityaiconfirmed"
                variant="accent"
                :data-test="getTestId('consent-datasecurity-ai-confirmed-checkbox')"
              />

              <MedLabel>
                {{ confirmationLabels["datasecurityaiconfirmed"] }}
              </MedLabel>

              <MedDescription v-if="getHelperText('datasecurityaiconfirmed')">
                {{ getHelperText("datasecurityaiconfirmed") }}
              </MedDescription>
            </MedFormField>
          </li>
        </ul>

        <hr />

        <template
          v-if="
            getConfirmation('datasecurityconfirmed') !== null &&
            getConfirmation('datasecurityresearchconfirmed') !== null &&
            getConfirmation('datasecurityaiconfirmed') !== null
          "
        >
          <p class="mt-4">Es liegen bereits alle Einwilligungen vor.</p>
        </template>
        <template v-else>
          <div class="flex items-end space-x-2">
            <ComponentInput
              v-model="form.city"
              label="Ort"
              placeholder="Bitte Wohnort eintragen"
              hide-clear
              :validation="form.errors.city"
            />
            <div class="w-1/2 pb-2 text-sm whitespace-nowrap">
              , den
              {{ new Date().toLocaleDateString("de-DE", {dateStyle: "medium"}) }}
            </div>
          </div>

          <div class="space-y-4">
            <MedFormField orientation="horizontal" size="mobile">
              <MedCheckbox
                v-model="form.isRepresentative"
                variant="accent"
                :data-test="getTestId('consent-is-representative-checkbox')"
              />

              <MedLabel>für {{ $page.props.patient?.name }} unterschreibt eine gesetzliche Vertretung</MedLabel>
            </MedFormField>

            <ComponentInput
              v-if="form.isRepresentative"
              v-model.lazy.trim="form.representative"
              :validation="form.errors.representative"
              hide-clear
              placeholder="Vor- und Nachname"
              label="Name gesetzliche Vertretung"
            />
          </div>

          <div role="group" aria-labelledby="signature-label">
            <span id="signature-label" class="text-sm text-gray-500" :class="{'text-mcred': form.errors.signature}">
              Unterschrift
            </span>
            <div ref="refSignature" class="mt-2 w-full">
              <div class="relative size-full">
                <canvas
                  role="img"
                  aria-label="Unterschriftsfeld; zeichnen Sie mit dem Finger oder der Maus Ihre Unterschrift."
                  tabindex="0"
                  class="size-full border-2 bg-yellow-100"
                  :class="{'border-mcred': form.errors.signature}"
                />

                <MedButton
                  :aria-label="isFullscreen ? 'Vollbildmodus verlassen' : 'Vollbildmodus aktivieren'"
                  flat
                  class="absolute top-2 right-2 z-10"
                  rounded="full"
                  :icon="isFullscreen ? 'close_fullscreen' : 'open_in_full'"
                  :data-test="getTestId('toggle-fullscreen-button')"
                  @click="isFullscreen ? exitFullscreen() : enterFullscreen()"
                />

                <div class="absolute bottom-0 h-18 w-full px-4">
                  <div class="flex items-center justify-between border-t-2 border-gray-600 py-2">
                    <span v-if="form.isRepresentative && form.representative">{{ form.representative }}</span>
                    <span v-else>{{ $page.props.patient?.name }}</span>
                    <div class="flex space-x-4">
                      <MedButton
                        icon="delete"
                        aria-label="Unterschrift löschen"
                        :data-test="getTestId('clear-signature-button')"
                        @click="clearSignature()"
                      >
                        Löschen
                      </MedButton>

                      <template v-if="isFullscreen">
                        <MedButton
                          variant="primary"
                          aria-label="Unterschrift übernehmen und Vollbildansicht schließen"
                          :data-test="getTestId('exit-fullscreen-button')"
                          @click="exitFullscreen()"
                        >
                          Fertig
                        </MedButton>
                      </template>
                    </div>
                  </div>
                </div>

                <div
                  v-if="isFullscreen"
                  class="absolute top-0 left-0 hidden size-full bg-mcred/80 p-8 text-xl font-semibold text-white portrait:block"
                >
                  Bitte drehen Sie Ihr Endgerät ins Querformat.
                </div>
              </div>
            </div>
            <p v-if="form.errors.signature" class="mt-2 text-xs text-mcred">{{ form.errors.signature }}</p>
          </div>

          <div class="space-y-4">
            <MedFormField orientation="horizontal" size="mobile" :error="form.errors.copy">
              <MedCheckbox
                v-model="form.copy"
                large
                variant="accent"
                :data-test="getTestId('consent-copy-email-checkbox')"
              />

              <MedLabel>Dokument per E-Mail senden</MedLabel>
            </MedFormField>

            <ComponentInput
              v-if="form.copy"
              v-model="form.email"
              hide-clear
              label="E-Mail-Adresse"
              placeholder="name@domain.de"
              :validation="form.errors.email"
            />
          </div>

          <p>Sie können Ihre Einwilligung jederzeit zurückziehen. Wenden Sie sich dazu an Ihre Apotheke.</p>

          <div class="flex flex-row-reverse">
            <MedButton
              :is-loading="form.processing"
              variant="primary"
              type="submit"
              :data-test="getTestId('submit-consent-button')"
            >
              Einwilligung unterschreiben
            </MedButton>
          </div>
        </template>
      </form>
    </template>
  </div>
</template>
